import { useEffect } from 'react';
import { type DefaultValues, type FieldValues, useForm, useWatch } from 'react-hook-form';

import { useTypedTranslation } from '~/shared/hooks';
import type { FormBlockPropsType } from '~/shared/types/form-type';

const useFormBlock = <TFieldValues extends FieldValues>({
  initialValues,
  isClearOnResetForm = false,
  isResetForm = false,
  setIsResetForm,
  shouldUnregister = true,
  formConfig,
}: FormBlockPropsType<TFieldValues>) => {
  const normalizedInitialValues =
    initialValues === undefined ? undefined : (initialValues as DefaultValues<TFieldValues>);

  const methods = useForm<TFieldValues>({
    defaultValues: normalizedInitialValues,
    mode: 'onChange',
    shouldUnregister,
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    getValues,
    reset,
    setValue,
    formState: { errors },
    control,
  } = methods;

  const watchField = useWatch({
    control,
    name: (formConfig.watchField ?? '_id') as never,
  });
  const t = useTypedTranslation(['text']);

  useEffect(() => {
    if ((isResetForm && normalizedInitialValues) || isClearOnResetForm) {
      reset(isClearOnResetForm ? ({} as DefaultValues<TFieldValues>) : normalizedInitialValues);
      setIsResetForm?.(false);
    }
  }, [isClearOnResetForm, isResetForm, normalizedInitialValues, reset, setIsResetForm]);

  useEffect(() => {
    if (formConfig.watchField && formConfig.handleWatchFieldChange && watchField) {
      formConfig.handleWatchFieldChange(
        setValue,
        getValues,
        watch,
        (normalizedInitialValues ?? {}) as Partial<TFieldValues>,
      );
    }
  }, [formConfig, getValues, normalizedInitialValues, setValue, watch, watchField]);

  useEffect(() => {
    if (formConfig.isUpdateForm) {
      formConfig.handleIsUpdateForm?.(setValue, getValues);
    }
  }, [formConfig, getValues, setValue]);

  return {
    handleSubmit,
    methods,
    watch,
    formConfig,
    errors,
    reset,
    t,
    initialValues: normalizedInitialValues,
    control,
    setValue,
    getValues,
  };
};

export default useFormBlock;
